import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Observable, Subject, throwError } from 'rxjs';
import { tap, takeUntil, catchError, finalize } from 'rxjs/operators';

import { SalesOrderUpdateStatusFormComponent } from 'v2/modules/sales-order/components';
import { ISalesOrderUpdateStatusFormResult } from 'v2/modules/sales-order/components/sales-order-update-status-form/sales-order-update-status-form.component';

import {
  PicklistPackingHoldOrderPopupComponent,
  PicklistPackingHoldOrderPopupSuccessComponent,
} from '../';
import { ILocation } from '../../../location';
import { IOrderSalesPicklistPreview } from '../../../order/interfaces/order-picklist-preview';
import {
  DialogConfig,
  LocationApiService,
  PackingFacadeService,
  PicklistApiService,
  ShipstationApiService,
  SnackBarConfig,
} from '../../../shared';
import { trackByValue } from '../../../shared/util';
import { exportB64toPdf } from '../../../shared/util/export.utils';
import {
  BulkShippingLabelComponent,
  ShippingLabelPopupComponent,
  ShippingLabelWithPackingSlipPopupComponent,
} from '../../../shipping';
import {
  PickListInterface,
  PicklistPacking,
  PicklistStatus,
  PicklistSalesOrderStatus,
  IPicklistOrderShipment,
  EPicklistOderStatus,
} from '../../interfaces';
import { PicklistVerifyComponent } from '../picklist-verify/picklist-verify.component';

@Component({
  selector: 'lum-picklist-packing',
  templateUrl: './picklist-packing.component.html',
  styleUrls: ['./picklist-packing.component.scss'],
})
export class PicklistPackingComponent implements OnInit, OnDestroy {
  @Output()
  exportShippingLabelEvent = new EventEmitter<{
    picklistId :number;
    orderIds ?:number[];
  }>();

  @Output()
  closeShippingLabelModalEvent = new EventEmitter<boolean>();

  currentPicklist :PickListInterface | null;
  currentPicklist$ :Observable<PickListInterface | null>;
  currentPicklistPacking :PicklistPacking[] | null;
  currentPicklistPacking$ :Observable<PicklistPacking[]>;
  loading$ :Observable<boolean>;
  updatingStatus$ :Observable<boolean>;
  currentTempLocation :ILocation;
  currentTempLocation$ :Observable<ILocation>;
  picklistId :number;
  orderNumberMaxDisplay :number = 4;
  selectedItems :PicklistPacking[] = [];
  loading :boolean = false;
  packFinishLoading :boolean = false;
  loadingShipItems :{ [id :number] :boolean } = {};
  loadingBulkShip :boolean = false;
  trackByValue = trackByValue;

  picklistOrderStatus = EPicklistOderStatus;

  private onDestroy$ = new Subject();

  constructor(
    public dialog :MatDialog,
    private route :ActivatedRoute,
    private picklistService :PicklistApiService,
    private locationService :LocationApiService,
    private snackBar :MatSnackBar,
    private shipstationService :ShipstationApiService,
    private cd :ChangeDetectorRef,
    private router :Router,
    private packingFacadeService :PackingFacadeService,
  ) {}

  ngOnInit() :void {
    this.getFromService();
    this.getRouterParams().subscribe();
    this.getPicklist().subscribe();
    this.getCurrentPicklistPacking().subscribe();

    this.getCurrentTempLocation().subscribe();
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  shipItem(salesOrderId :number) :void {
    const salesOrder = this.currentPicklist?.orders.find(
      (order) => order.id === salesOrderId,
    );
    this.loadingShipItems = { ...this.loadingShipItems, [salesOrderId]: true };
    this.loadingBulkShip = true;

    if (salesOrder?.id) {
      const hasShipments = salesOrder.shipments?.length > 0;
      const wasPushedToOms = salesOrder.destinationAppIntegrationAccountId !== null;

      if (!hasShipments && !wasPushedToOms) {
        this.dialog
          .open(SalesOrderUpdateStatusFormComponent, {
            data: {
              title: 'Ship Item',
              orderStatus: [],
              hideAdditionalFields: false,
              source: 'picklist',
            },
            width: DialogConfig.sizes.small,
          })
          .afterClosed()
          .pipe(takeUntil(this.onDestroy$))
          .subscribe((result :ISalesOrderUpdateStatusFormResult) => {
            if (result) {
              const { carrierCode, trackingNumber } = result;
              this.shipSingleItem({
                salesOrderId,
                picklistId: this.picklistId,
                trackingNumber,
                carrierCode,
              }).subscribe();
            } else {
              this.loadingShipItems = {
                ...this.loadingShipItems,
                [salesOrderId]: false,
              };
              this.loadingBulkShip = false;
            }
          });

        return;
      }
    }
    this.shipSingleItem({
      salesOrderId,
      picklistId: this.picklistId,
      trackingNumber: null,
      carrierCode: null,
    }).subscribe();
    this.cd.detectChanges();
  }

  holdItem(salesOrderId :number) :void {
    const dialogRef = this.dialog.open(PicklistPackingHoldOrderPopupComponent);

    dialogRef.afterClosed().subscribe((value) => {
      if (!value) {
        value = new Date().setUTCHours(0, 0, 0, 0);
      }
      const date = new Date(value);
      this.picklistService
        .holdPackingItem({
          salesOrderId,
          holdUntilDate: date.toDateString(),
        })
        .pipe(
          tap((res) => {
            this.dialog.open(PicklistPackingHoldOrderPopupSuccessComponent, {
              data: res,
            });
            this.loadPicklist();
            this.loadPicklistPacking();
          }),
          takeUntil(this.onDestroy$),
        )
        .subscribe();
    });
  }

  exportShippingLabels() :void {
    this.exportShippingLabelEvent.emit({ picklistId: this.picklistId });
  }

  bulkExportShippingLabels(withPackingSlip ?:boolean) :void {
    if (!this.selectedItems.length) return;

    const orderIds = this.selectedItems.map((item) => item.id);
    this.exportShippingLabelEvent.emit({
      picklistId: this.picklistId,
      orderIds,
    });
  }

  bulkExportShippingLabelsWithPackingSlip() :void {
    if (!this.selectedItems.length) return;
    const salesOrderIds = this.selectedItems.map((item) => item.id);

    this.dialog.open(ShippingLabelWithPackingSlipPopupComponent, {
      data: { salesOrderIds },
    });
  }

  exportPackingSlip(salesOrderId :number) :void {
    this.packingFacadeService.exportPackingSlipForOrdersToPdf([salesOrderId]);
  }

  printLabel(salesOrderId :number) :void {
    this.dialog.open(ShippingLabelPopupComponent, { data: { salesOrderId } });
  }

  printLabelWithPackingSlip(salesOrderId :number) :void {
    this.dialog.open(ShippingLabelPopupComponent, {
      data: { salesOrderId, withPackingSlip: true },
    });
  }

  onFinishPicklist() :void {
    if (this.currentPicklist) {
      this.packFinishLoading = true;
      this.picklistService
        .updatePicklistStatus({
          id: this.currentPicklist.id,
          picklistStatus: PicklistStatus.PACKED,
        })
        .pipe(takeUntil(this.onDestroy$))
        .subscribe({
          next: (response :{ success :string }) => {
            this.snackBar.open(response.success, 'Success', {
              duration: SnackBarConfig.DEFAULT.duration,
            });
            if (this.currentPicklist) {
              this.currentPicklist.status = PicklistStatus.PACKED;
            }
            this.packFinishLoading = false;
          },
          error: (response) => {
            this.snackBar.open(response.error, 'Fail', {
              duration: SnackBarConfig.DEFAULT.duration,
            });
            this.packFinishLoading = false;
          },
        });
    }
  }

  onVerifyPacking(packing :PicklistPacking) :void {
    this.dialog
      .open(PicklistVerifyComponent, {
        data: {
          packing,
        },
        width: DialogConfig.sizes['extra-large'],
        disableClose: true,
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((res) => {
        if (res) {
          // TODO: Update packing data after verify
          // If there are any api call must be done here
          this.picklistService
            .verifyScanItems({ picklistOrderId: packing.picklistOrderId })
            .pipe(
              tap((res) => {
                this.loadPicklistPacking();
                this.selectedItems = [];
              }),
              takeUntil(this.onDestroy$),
            )
            .subscribe();
        }
      });
  }

  printShippingLabels() :void {
    if (this.currentPicklistPacking && this.currentPicklistPacking.length > 0) {
      this.loading = true;
      const salesOrderIds = this.selectedItems?.length
        ? this.selectedItems.map((item) => item.id)
        : [];

      this.getPicklistItemsBySalesOrderIds(salesOrderIds);
    }
  }

  private getPicklistItemsBySalesOrderIds(salesOrderIds :number[] = []) :void {
    this.picklistService
      .getPickingItemsBySalesOrderIds({
        salesOrderIds: salesOrderIds.join(','),
        includeWithPicklist: 'true',
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response :IOrderSalesPicklistPreview) => {
          this.loading = false;
          this.cd.detectChanges();

          this.openShippingLabelComponent(response);
        },
        error: (response) => {
          this.loading = false;
          this.cd.detectChanges();

          this.snackBar.open(response.error, 'Fail', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
  }

  private openShippingLabelComponent(response :IOrderSalesPicklistPreview) :void {
    this.dialog
      .open(BulkShippingLabelComponent, {
        data: {
          orders: response.orders ?? [],
          warehouseAddress: response.warehouseAddress ?? null,
        },
        width: DialogConfig.sizes['xxx-large'],
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((response) => {
        if (response) {
          this.closeShippingLabelModalEvent.emit(true);

          if (this.currentPicklist?.id) {
            this.router.navigate(['shipping/picklists', this.currentPicklist?.id]);
          } else {
            this.router.navigate(['shipping/picklists']);
          }
        }
      });
  }

  private getRouterParams() :Observable<Params> {
    return this.route.params.pipe(
      tap((params :Params) => {
        const { id } = params;
        if (id) {
          this.picklistId = id;
          this.loadPicklist();
          this.loadTempLocation();
          this.loadPicklistPacking();
        }
      }),
      takeUntil(this.onDestroy$),
    );
  }

  private loadPicklist() :void {
    this.picklistService.getById({
      id: this.picklistId,
      params: {
        relations: 'orders.flaggedTag,orders.shipments',
      },
    });
  }

  private loadPicklistPacking() :void {
    this.picklistService.getPicklistPackingItems(this.picklistId);
  }

  private loadTempLocation() :void {
    this.locationService.getTempLocation({
      id: this.picklistId,
      params: {
        relations: 'parent.warehouseGroups',
      },
    });
  }

  private getFromService() :void {
    this.loading$ = this.picklistService.loading$;
    this.currentTempLocation$ = this.locationService.currentTempLocation$;
    this.updatingStatus$ = this.picklistService.updatingStatus$;
    this.currentPicklistPacking$ = this.picklistService.currentPicklistPacking$;
    this.currentPicklist$ = this.picklistService.currentPicklist$;
  }

  private getCurrentPicklistPacking() :Observable<PicklistPacking[] | null> {
    return this.currentPicklistPacking$.pipe(
      tap((picklistPacking :PicklistPacking[] | null) => {
        this.currentPicklistPacking = picklistPacking;
      }),
      takeUntil(this.onDestroy$),
    );
  }
  private getPicklist() :Observable<PickListInterface | null> {
    return this.currentPicklist$.pipe(
      tap((picklist :PickListInterface | null) => {
        this.currentPicklist = picklist;
      }),
      takeUntil(this.onDestroy$),
    );
  }

  private getCurrentTempLocation() :Observable<ILocation> {
    return this.currentTempLocation$.pipe(
      tap((response) => {
        this.currentTempLocation = response;
      }),
      takeUntil(this.onDestroy$),
    );
  }

  get showPicklistActions() :boolean {
    if (!this.currentPicklist) return false;

    return this.currentPicklist.status !== PicklistStatus.PACKED;
  }

  get canFinishPicklist() :boolean {
    if (!this.currentPicklistPacking) {
      return false;
    }
    const shippableOrderIdx :number = this.currentPicklistPacking.findIndex(
      (picklistPacking :PicklistPacking) => picklistPacking.shippable,
    );

    return shippableOrderIdx < 0;
  }

  get orderNumbers() :string[] {
    if (this.currentPicklist?.orders?.length) {
      return this.currentPicklist.orders.map((order) => order.orderNumber);
    }

    return [];
  }

  flagOrder(salesOrderId :number) :void {
    this.picklistService
      .flagSalesOrder({ salesOrderId })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response :{ success :string }) => {
          this.loadPicklist();
          this.loadPicklistPacking();
          this.snackBar.open(response.success, 'Success', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
        error: (response) => {
          this.snackBar.open(response.error, 'Fail', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
  }

  onSelectionChange(items :PicklistPacking[]) :void {
    this.selectedItems = items;
  }

  get checkedItemReadyToShipCount() :number {
    const warehouseGroup =
      this.currentTempLocation?.parent?.warehouseGroups?.[0] ?? null;

    return this.selectedItems.filter(
      (item) =>
        (item.checked &&
          (warehouseGroup && !warehouseGroup.pickAndPackEnabled
            ? true
            : item.orderStatus !== EPicklistOderStatus.SHIPPED) &&
          (item.scanToVerify ? item.verified : true)) ||
        item.shippable,
    ).length;
  }

  get checkedItemNeedToVerifyCount() :number {
    return this.selectedItems.filter((item) => item.checked && !item.verified)
      .length;
  }

  bulkVerify() :void {
    const data = this.selectedItems.filter(
      (item) => item.checked && !item.verified,
    );

    const picklistOrderIds = data.map((item) => item.picklistOrderId);

    this.picklistService
      .bulkVerifyScanItems({ picklistOrderIds: picklistOrderIds })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response) => {
          this.loadPicklistPacking();
          this.selectedItems = [];
        },
        error: (response) => {
          this.snackBar.open(response.error, 'Fail', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
  }

  bulkShip() :void {
    const warehouseGroup =
      this.currentTempLocation?.parent?.warehouseGroups?.[0] ?? null;
    const data = this.selectedItems.filter(
      (item) =>
        item.checked &&
        (warehouseGroup && !warehouseGroup.pickAndPackEnabled
          ? true
          : item.orderStatus !== EPicklistOderStatus.SHIPPED) &&
        (item.scanToVerify ? item.verified : true) &&
        item.shippable,
    );

    const salesOrderIds = data.map((item) => item.id);
    if (salesOrderIds.length <= 0) {
      this.snackBar.open('Selected items arent available to ship.', 'Fail', {
        duration: SnackBarConfig.DEFAULT.duration,
      });

      return;
    }

    if (salesOrderIds.length === 1) {
      this.shipItem(salesOrderIds[0]);

      return;
    }
    // Set loading state for bulk shipping
    this.loadingBulkShip = true;

    this.picklistService
      .bulkShipPackingItem({
        salesOrderIds: salesOrderIds,
        picklistId: this.picklistId,
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response) => {
          this.loadPicklist();
          this.loadPicklistPacking();
          this.selectedItems = [];

          // Reset loading state after completion
          this.loadingBulkShip = false;
        },
        error: (response) => {
          this.snackBar.open(response.error, 'Fail', {
            duration: SnackBarConfig.DEFAULT.duration,
          });

          // Reset loading state after completion
          this.loadingBulkShip = false;
        },
      });
  }

  private shipSingleItem(payload :{
    salesOrderId :number;
    picklistId :number;
    trackingNumber ?:string;
    carrierCode ?:string;
  }) :Observable<any> {
    const { salesOrderId } = payload;

    return this.picklistService.shipPackingItem(payload).pipe(
      tap((res) => {
        this.loadPicklist();
        this.loadPicklistPacking();
        this.selectedItems = [];
      }),
      finalize(() => {
        this.loadingShipItems[salesOrderId] = false;
        this.loadingBulkShip = false;
      }),
      takeUntil(this.onDestroy$),
    );
  }
}
